import { Controller } from '@hotwired/stimulus'
import { LocalClient } from '../clients'
import { targetUpdate } from './components/component_controller'
import { DispatchEvent, SelectOption } from '../types/'

interface OptionsFromApi {
  options: SelectOption[]
}

export class NewDeviceFormController extends Controller {
  static targets = [
    'organization',
    'deviceType',
    'serialNumber',
    'location',
    'site',
    'placement',
    'warrantyExpiresOnHelpText',
    'datePicker',
  ]

  static values = {
    warrantyExpiresOnHelpTextDefault: String,
  }

  declare readonly organizationTarget: HTMLInputElement
  declare readonly deviceTypeTarget: HTMLInputElement
  declare readonly serialNumberTarget: HTMLInputElement
  declare readonly locationTarget: HTMLSelectElement
  declare readonly siteTarget: HTMLSelectElement
  declare readonly placementTarget: HTMLInputElement
  declare readonly warrantyExpiresOnHelpTextTarget: HTMLInputElement
  declare readonly datePickerTarget: HTMLElement

  declare readonly warrantyExpiresOnHelpTextDefaultValue: string

  declare deviceTypeId: string | null
  declare locationId: string | null
  declare organizationId: string | null

  public async handleOrganizationSelect(event: DispatchEvent): Promise<void> {
    const organizationId = event.detail.value
    if (organizationId != this.organizationId) {
      this.organizationId = organizationId
      this.locationId = null
      const locations = await this.getOrganizationLocations()

      this.setLocationSelectOptions(locations.options)
      this.disableSiteSelect()
      this.disablePlacementField()
    }
  }

  public async handleLocationSelect(event: DispatchEvent): Promise<void> {
    const locationId = event.detail.value
    if (locationId != this.locationId) {
      this.locationId = locationId
      this.disableSiteSelect()
      this.disablePlacementField()

      if (!locationId) {
        this.updateTimeZone('UTC')
        return
      }

      const [sites, location] = await Promise.all([
        this.getLocationSites(),
        this.getLocation(locationId),
      ])

      this.setSiteSelectOptions(sites.options)
      this.updateTimeZone(location.physical_address?.time_zone || 'UTC')
    }
  }

  private async getLocation(locationId: string) {
    if (!locationId) {
      return { physical_address: {} }
    }
    return fetch(`/locations/${locationId}.json`).then((response) =>
      response.json(),
    )
  }

  private updateTimeZone(timeZone: string) {
    targetUpdate(this.datePickerTarget, {
      time_zone: timeZone,
    })
  }

  public handleSiteSelect(event: DispatchEvent): void {
    this.enablePlacementField()
  }

  public handleEquipmentTypeSelect(event: DispatchEvent): void {
    const deviceTypeId = event.detail.value
    if (deviceTypeId != this.deviceTypeId) {
      this.deviceTypeId = deviceTypeId
      if (this.deviceTypeId) {
        fetch(`/device_types/${this.deviceTypeId}`)
          .then((response) => response.json())
          .then((data) => {
            const {
              device_type: { warranty_expires_on_help_text },
            } = data
            this.warrantyExpiresOnHelpTextTarget.innerText =
              warranty_expires_on_help_text
          })
      } else {
        this.warrantyExpiresOnHelpTextTarget.innerText =
          this.warrantyExpiresOnHelpTextDefaultValue
      }
    }
  }

  public clearForm() {
    this.clearOrganizationCascader()
    this.clearDeviceTypeCascader()

    this.setSerialNumberValue('')
    this.setPlacementValue('')

    this.disableLocationSelect()
    this.disableSiteSelect()
    this.disablePlacementField()
  }

  // form state

  private clearOrganizationCascader(): void {}
  private clearDeviceTypeCascader(): void {}

  private disableLocationSelect(): void {
    targetUpdate(this.locationTarget, {
      options: [],
      disabled: true,
      value: null,
    })
  }

  private setLocationSelectOptions(options: SelectOption[]): void {
    targetUpdate(this.locationTarget, {
      options: options,
      disabled: false,
      value: null,
    })
  }

  private disableSiteSelect(): void {
    targetUpdate(this.siteTarget, { options: [], disabled: true, value: null })
  }

  private setSiteSelectOptions(options: SelectOption[]): void {
    targetUpdate(this.siteTarget, {
      options: options,
      disabled: false,
      value: null,
    })
  }

  private disablePlacementField(): void {
    this.placementTarget.value = ''
    this.placementTarget.setAttribute('disabled', 'true')
    this.placementTarget.setAttribute('readonly', 'true')
  }
  private enablePlacementField(): void {
    this.placementTarget.removeAttribute('disabled')
    this.placementTarget.removeAttribute('readonly')
  }

  private setSerialNumberValue(value: string): void {}
  private setPlacementValue(value: string): void {}

  // network

  private async getOrganizationLocations(): Promise<OptionsFromApi> {
    if (!this.organizationId) {
      return { options: [] }
    }

    const path = '/organization_locations'
    const params = `organization_id=${this.organizationId}`
    return await LocalClient.queryPath({ path, params })
  }

  private async getLocationSites(): Promise<OptionsFromApi> {
    if (!this.locationId) {
      return { options: [] }
    }

    const path = `/locations/sites`
    const params = `location_ids=${this.locationId}`
    return await LocalClient.queryPath({ path, params })
  }
}
